<template>
    <sub-section title="What do the annual payments solely from my pension pots look like?" :showHelp="false">
        <p v-if="isTakingTaxFreeLumpSum">
            Based on the pots you wish to transfer, the very first payment from these will be the 25% tax free cash lump sum wanted
            at retirement. This is currently estimated as {{ formatter.formatCurrency(currentAllocations.taxFreeCashLumpSum) }}.
        </p>

        <p>
            All the annual pension pot payments currently wanted in your plan are shown in the chart below.
        </p>

        <p>
            <strong>There are currently {{ unmatchedPaymentYearsCount }} years of payments shown in dark green.</strong> These expected payments
            depend on selling assets from the {{ fundInfo.responsibleGrowthFund.name }} and investment returns from all funds.
        </p>

        <p>
            <strong>There are currently {{ matchedPaymentsModel }} years of payments shown in a lighter green colour.</strong> These expected
            payments can be met solely from the estimated funds in the {{ fundInfo.moneyFund.name }} and {{ fundInfo.incomeFund.name }} at retirement
            in the GuiidePath product.<template v-if="isTakingTaxFreeLumpSum"> This includes any lump sum payment also.</template>
        </p>

        <div class="row align-items-center mb-3">
            <div class="col-md-12">
                <drawdown-chart :chartData="drawdownChartPoints"></drawdown-chart>
            </div>
        </div>

        <p style="font-size: 1.25rem !important;"><strong>Understanding investment risk in drawdown</strong></p>

        <p>
            Most people need to rely on some investment return (and hence take risk) for all their expected payments to be met. You can choose any 
            combination of the three types of Funds offered within {{ Constants.Product.Name }} with the rest, which you can model below. You can find more information on
            these type of funds and building a portfolio from <a href="https://www.moneyhelper.org.uk/en/pensions-and-retirement/building-your-retirement-pot/investing-in-retirement" target="_blank">MoneyHelper</a>.
        </p>

        <p>
            <strong>{{ FundAllocationCalculator.TrusteeAccountPercentage }}% of your total funds will always be held in the trustee bank account which is
            currently {{ formatter.formatCurrency(currentAllocations.trusteeBankAccount) }} based on the expected transfers.</strong>
        </p>

        <div class="row align-items-center mb-3">
            <div class="col-md-4">
                <label for="moneyFund" class="form-label mb-0">
                    <strong>{{ fundInfo.moneyFund.name }}</strong>:
                </label>
            </div>
            <div class="col-md-3">
                <number-input
                    ref="currentAllocToMoneyFundInput"
                    :description="fundInfo.moneyFund.name"
                    v-model="currentAllocToMoneyFund"
                    :max="10000000"
                    :showLabel="false">
                </number-input>
            </div>
        </div>

        <div class="row align-items-center mb-3">
            <div class="col-md-4">
                <label for="moneyFund" class="form-label mb-0">
                    <strong>{{ fundInfo.incomeFund.name }}</strong>:
                </label>
            </div>
            <div class="col-md-3">
                <number-input
                    ref="currentAllocToIncomeFundInput"
                    :description="fundInfo.incomeFund.name"
                    v-model="currentAllocToIncomeFund"
                    :max="10000000"
                    :showLabel="false">
                </number-input>
            </div>
        </div>

        <div class="row align-items-center mb-4">
            <div class="col-md-4">
                <label for="moneyFund" class="form-label mb-0">
                    <strong>{{ fundInfo.responsibleGrowthFund.name }}</strong>:
                </label>
            </div>
            <div class="col-md-8">
                {{ formatter.formatCurrency(currentAllocations.growthFund < 0 ? 0 : currentAllocations.growthFund) }}
                <span v-if="overallocatedAmount > 0" class="text-danger ms-2">(Over-allocated by {{ formatter.formatCurrency(overallocatedAmount) }})</span>
            </div>
        </div>

        <p><strong>It is key to understand your reliance on positive returns and more risky growth funds as downturns can occur at any time.</strong></p>

        <p>
            As you change the allocations above, the expected chart payments may change to a lighter colour. If they do, it shows these payments could be
            met using the expected value in the Money and Income funds at Retirement alone. The more in these funds the more payments change colour.
        </p>

        <p>
            <strong>The fewer that change colour the higher risk future payments may not be met.</strong> These rely on payments from growth funds and overall 
            future investment returns. There is more risk if payments in the <a href="https://www.investopedia.com/terms/s/sequence-risk.asp" target="_blank">early 
            years after retirement</a> do not change colour. Having to sell funds when they have fallen in value near the start of retirement can be very 
            difficult to recover from.
        </p>
    </sub-section>
</template>

<script setup lang="ts">
import { ref, computed, watch, PropType } from "vue";
import { Retiree } from "../../ts/retiree";
import { RetirementPlanResponse } from "@/guiide/ts/response/retirementPlanResponse";
import { useStore } from "vuex";
import { getPensionPotDrawdownAmounts } from "@/guiide/ts/utilities";
import { formatter } from "@/shared/ts/formatter";
import fundInfo from "@/guiide/ts/funds";
import Constants from "@/guiide/ts/constants";
import PatternFillType from "@/guiide/ts/charts/patternFill";
import patternFills from "@/guiide/ts/charts/patternFills";
import { PointOptionsObject } from "highcharts";
import SubSection from "../common/SubSection.vue";
import DrawdownChart from "../charts/DrawdownChart.vue";
import FundAllocationCalculation from "@/guiide/ts/secure/fundAllocationCalculation";
import NumberInput from "../common/NumberInput.vue";
import { CurrentAllocations } from "@/guiide/ts/integration/currentAllocations";
import FundAllocationCalculator from "@/guiide/ts/secure/fundAllocationCalculator";
import { FundAllocationInstruction } from "@/guiide/ts/secure/fundAllocationInstruction";
import { PensionPot } from "@/guiide/ts/pensionPot";

const props = defineProps({
    retiree: { type: Object as () => Retiree | null, default: () => null, },
    selectedPensionPotIds: { type: Array as PropType<Array<number>>, default: () => [] }
});

const emit = defineEmits<{(e: 'invalid', reason: string): void;}>();

defineExpose({ getAllocationInstruction });

const store = useStore();

const matchedYearColor = Constants.Color.lightEmerald;
const unmatchedYearColor = Constants.Color.brunswickGreen;

const matchedPaymentsModel = ref(Constants.Product.MatchedYearsMin);
const drawdownChartPoints = ref<Array<PointOptionsObject>>(new Array<PointOptionsObject>());

const currentAllocToMoneyFund = ref(0);
const currentAllocToIncomeFund = ref(0);
let currentAllocations = ref<CurrentAllocations>(new CurrentAllocations());
const overallocatedAmount = ref(0);

watch(
    () => [currentAllocToMoneyFund.value, currentAllocToIncomeFund.value],
    () => {
        currentAllocations.value.updateAllocations(currentAllocToMoneyFund.value, currentAllocToIncomeFund.value);

        const isOverallocated = currentAllocations.value.growthFund < 0;
        overallocatedAmount.value = isOverallocated ? Math.abs(currentAllocations.value.growthFund) : 0;

        let matchedYears = 0;

        if (!isOverallocated) {
            let remainingLowRiskAllocation = totalLowRiskAllocationAtRetirement.value - currentAllocations.value.taxFreeCashLumpSum;

            for (const drawdownAmount of pensionPotDrawdownAmounts.value) {
                remainingLowRiskAllocation -= drawdownAmount;

                if (remainingLowRiskAllocation < 0) {
                    break;
                }

                matchedYears++;
            }
        }

        matchedPaymentsModel.value = matchedYears;
    }
);

const potsToTransfer = computed<Array<PensionPot>>(() => {
    if (!props.retiree) {
        return [];
    }

    return props.retiree.pensionPots.pots.filter(pp => props.selectedPensionPotIds.includes(pp.id));
});

watch(
    () => props.selectedPensionPotIds,
    () => {
        if (!props.retiree) {
            return;
        }

        const estCurrentValue = potsToTransfer.value.reduce((total, pp) => total + pp.estimatedValueAtPresent, 0);

        let taxFreeCashLumpSum = potsToTransfer.value.reduce((total, pp) => total + pp.lumpSumValueAtRetirement, 0);
        taxFreeCashLumpSum = Math.min(Constants.TaxFreeCashLimit, taxFreeCashLumpSum);

        currentAllocations.value.initialise(estCurrentValue, taxFreeCashLumpSum);

        currentAllocToMoneyFund.value = 0;
        currentAllocToIncomeFund.value = 0;
    },
    { immediate: true }
);

const totalEstimatedValueAtRetirement = computed<number>(() => potsToTransfer.value.reduce((total, pp) => total + pp.estimatedValueAtRetirement, 0));

const totalLowRiskAllocationAtRetirement = computed<number>(() => {
    if (overallocatedAmount.value > 0) {
        return 0;
    }

    return totalEstimatedValueAtRetirement.value * currentAllocations.value.totalLowRiskPercentage;
});

const isTakingTaxFreeLumpSum = computed<boolean>(() => store.state.retiree?.requirements.takingTaxFreeAsLumpSum ?? false);

const pensionPotDrawdownAmounts = computed<Array<number>>(() => getPensionPotDrawdownAmounts(store.state.plan));

const retirementPlan = computed<RetirementPlanResponse>(() => store.state.plan);

const unmatchedPaymentYearsCount = computed<number>(() => pensionPotDrawdownAmounts.value.length - matchedPaymentsModel.value);

watch(
    () => [
        store.state.retiree,
        matchedPaymentsModel.value,
        retirementPlan.value,
        pensionPotDrawdownAmounts.value,
        currentAllocations.value,
        totalLowRiskAllocationAtRetirement.value
    ],
    () => { redraw(); },
    { deep: true, immediate: true }
);

function redraw() {
    if (store.state.retiree == null || retirementPlan.value == null && store.state.plan != null) {
        return;
    }

    updateDrawdownChart();
}

function updateDrawdownChart() {
    drawdownChartPoints.value.length = 0;

    pensionPotDrawdownAmounts.value.map((a, i) => {
        const isMatchedYear = i < matchedPaymentsModel.value;
        const colorName = isMatchedYear ? matchedYearColor : unmatchedYearColor;
        const strokeWidth = 3;
        const pointOptionsObject = getDataPoint(a, colorName, PatternFillType.None, strokeWidth);
        drawdownChartPoints.value.push(pointOptionsObject);
    });

    return drawdownChartPoints;
};

function getAllocationInstruction(): FundAllocationInstruction {
    const fundAllocationCalcAtPresent = currentAllocations.value.getFundAllocationCalculation();
    const fundAllocationAtRetirement: FundAllocationCalculation = getAllocationInformationAtRetirement();

    const taxFreeLumpSumFundId: number|null = isTakingTaxFreeLumpSum.value ? fundInfo.moneyFund.id : null;

    let isValid = true;
    let invalidMessage = "";
    if (overallocatedAmount.value > 0) {
        isValid = false;
        invalidMessage = "Please check your fund allocations as they are currently over-allocated.";
    }

    return { 
        matchedYears: matchedPaymentsModel.value,
        matchedYearsInWindow: 0,
        taxFreeLumpSumFundId: taxFreeLumpSumFundId,
        fundAllocCalcAtPresent: fundAllocationCalcAtPresent,
        fundAllocCalcAtRetirement: fundAllocationAtRetirement,
        isValid,
        invalidMessage
    };
}

function getAllocationInformationAtRetirement(): FundAllocationCalculation {    
    const retirementAllocations = new CurrentAllocations();
    retirementAllocations.initialise(totalEstimatedValueAtRetirement.value, currentAllocations.value.taxFreeCashLumpSum);

    const moneyFund = totalEstimatedValueAtRetirement.value * (currentAllocations.value.moneyFundPercentage / 100);
    const incomeFund = totalEstimatedValueAtRetirement.value * (currentAllocations.value.incomeFundPercentage / 100);
    retirementAllocations.updateAllocations(moneyFund, incomeFund);

    return retirementAllocations.getFundAllocationCalculation();
}

function getDataPoint(y: number, colorName: string, patternFillType: PatternFillType, strokeWidth: number, name: string = ''): PointOptionsObject {
    let color: any = getPatternFillObject(patternFillType, colorName, strokeWidth);
    return { y, color, name };
}

function getPatternFillObject(patternFillType: PatternFillType, colorName: string, strokeWidth: number) {
    if (patternFillType == PatternFillType.None) {
        return colorName;
    }

    return {
        pattern: {
            path: {
                d: patternFills.getByType(patternFillType),
                strokeWidth: strokeWidth
            },
            width: 10,
            height: 10,
            color: colorName,
            opacity: 1
        }
    }; 
}
</script>
